import { rtLogService } from '@rammetec/core'
import type { Page, RestReadContext } from '@rammetec/data-access'
import { partyDescriptor, type Party } from '@syscolect/domain'
import {
  filterAstToDataFilter,
  type DataColumnType,
} from './restScriptFilter'
import {
  extractMeta,
  extractRows,
  toParty,
  type VhJobsResponse,
} from './restScriptVectorhub'

/** Maps partyDescriptor field types → VH DataColumnType (memoized at module load). */
function partyFieldTypeLookup(): (field: string) => DataColumnType | undefined {
  const map = new Map<string, DataColumnType>()
  for (const field of partyDescriptor.fields) {
    const t = field.type
    if (t === 'number') map.set(field.name, 'number')
    else if (t === 'boolean') map.set(field.name, 'boolean')
    else if (t === 'date') map.set(field.name, 'date')
    else map.set(field.name, 'string') // includes array (roles CSV from VH)
  }
  return (field) => map.get(field)
}

const fieldType = partyFieldTypeLookup()

/**
 * RestScript `getAll` handler for Party — forwards pagination + flattened
 * `filterAst` to VectorHub `data.execute` and maps `job.result.meta` into a
 * {@link Page} envelope.
 */
export async function partyRestScriptGetAll(
  ctx: RestReadContext,
): Promise<Party[] | Page<Party>> {
  const orgId = ctx.fixedWhere.org
  if (!orgId) {
    throw new Error(
      '[restScriptPartyHandler] RestScript getAll requires org scope (fixedWhere.org).',
    )
  }

  if (ctx.tableName !== 'Party') return []

  const queryId = `data-${ctx.tableName.toLowerCase()}`
  const { limit, offset, countTotal, filterAst } = ctx.options ?? {}
  const page = limit != null ? { limit, offset: offset ?? 0 } : undefined
  const filter = filterAstToDataFilter(filterAst, fieldType)

  const res = await ctx.request<VhJobsResponse>('POST', '/jobs', {
    orgId,
    action: 'data.execute',
    params: {
      queryId,
      ...(page ? { page } : {}),
      ...(countTotal ? { countTotal: true } : {}),
      ...(filter ? { filter } : {}),
    },
    sync: true,
    syncTimeout: 30_000,
  })

  const items = extractRows(res.job).map(toParty)
  const meta = extractMeta(res.job)
  if (meta.ignoredFilters?.length) {
    rtLogService.warn('[restScriptPartyHandler] filtros ignorados pelo VH', {
      queryId,
      ignoredFilters: meta.ignoredFilters,
    })
  }

  if (!page && !countTotal) return items

  return {
    items,
    hasMore: meta.hasMore ?? false,
    total: meta.totalCount,
    offset: meta.offset ?? page?.offset ?? 0,
  }
}
